import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  FormContainer,
  Header,
  BackButton,
  Title,
  Form,
  InputGroup,
  Label,
  Input,
  InputRow,
  SubmitButton
} from './styles';
import { FiArrowLeft } from 'react-icons/fi';
import Tooltip from '../../../components/tooltip';
import { useLanguage } from '../../../contexts/LanguageContext';
import { api } from '../../../services/api';
import Loading from '../../../components/Loading';
import {
  DeleteModal,
  DeleteModalContent,
  DeleteModalButtons,
  CancelButton,
  ConfirmButton,
} from '../styles';
import { useTheme, styled } from 'styled-components';

const GreenConfirmButton = styled.button`
  padding: 8px 16px;
  border-radius: 4px;
  border: none;
  background-color: ${({ theme }) => theme.success};
  color: ${({ theme }) => theme.successOn};
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: ${({ theme }) => theme.success};
  }
`;

interface UserFormData {
  name: string;
  email: string;
  cnpj: string;
  contact_name: string;
  phone: string;
  role: string;
}

const OrganizationForm: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { id } = useParams(); // Pega o ID da URL se estiver editando
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [formData, setFormData] = useState<UserFormData>({
    name: '',
    email: '',
    cnpj: '',
    contact_name: '',
    phone: '',
    role: ''
  });
  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [cnpjError, setCnpjError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Se houver ID, busca os dados do usuário
  useEffect(() => {
    const fetchOrganization = async () => {
      if (!id) return; // Se não tiver ID, é modo de criação
      
      try {
        const token = localStorage.getItem('@App:token');
        const response = await api.get(`/api/organization/${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        // Alimenta o formulário com os dados da organização
        setFormData({
          name: response.data.nameOrganization,
          cnpj: response.data.cnpjTaxId,
          email: response.data.email,
          contact_name: response.data.nameContact,
          phone: response.data.phoneContact,
          role: response.data.role || ''
        });
      } catch (error) {
        console.error('Error fetching organization data:', error);
      }
    };

    fetchOrganization();
  }, [id]); // Executa quando o ID mudar

  const validateEmail = (email: string) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };
  
  const validatePhone = (phone: string) => {
    // Remove todos os caracteres não numéricos para validação
    const digitsOnly = phone.replace(/\D/g, '');
    // Verifica se tem pelo menos 10 dígitos (formato americano)
    return digitsOnly.length >= 10;
  };
  
  const formatPhone = (phone: string) => {
    // Remove todos os caracteres não numéricos
    const digitsOnly = phone.replace(/\D/g, '');
    
    if (digitsOnly.length === 0) return '';
    
    // Adiciona o "+" automaticamente quando o usuário digita o primeiro número
    if (digitsOnly.length <= 1) {
      return `+${digitsOnly}`;
    } else if (digitsOnly.length <= 4) {
      return `+${digitsOnly.slice(0, 1)} (${digitsOnly.slice(1)}`;
    } else if (digitsOnly.length <= 7) {
      return `+${digitsOnly.slice(0, 1)} (${digitsOnly.slice(1, 4)}) ${digitsOnly.slice(4)}`;
    } else {
      return `+${digitsOnly.slice(0, 1)} (${digitsOnly.slice(1, 4)}) ${digitsOnly.slice(4, 7)}-${digitsOnly.slice(7)}`;
    }
  };
  
  const validateCNPJ = (cnpj: string) => {
    // Remove todos os caracteres não numéricos para validação
    const digitsOnly = cnpj.replace(/\D/g, '');
    // Verifica se tem 14 dígitos
    return digitsOnly.length === 14;
  };
  
  const formatCNPJ = (cnpj: string) => {
    // Remove todos os caracteres não numéricos
    const digitsOnly = cnpj.replace(/\D/g, '');
    
    if (digitsOnly.length === 0) return '';
    
    // Formata o CNPJ conforme a quantidade de dígitos inseridos
    if (digitsOnly.length <= 2) {
      return digitsOnly;
    } else if (digitsOnly.length <= 5) {
      return `${digitsOnly.slice(0, 2)}.${digitsOnly.slice(2)}`;
    } else if (digitsOnly.length <= 8) {
      return `${digitsOnly.slice(0, 2)}.${digitsOnly.slice(2, 5)}.${digitsOnly.slice(5)}`;
    } else if (digitsOnly.length <= 12) {
      return `${digitsOnly.slice(0, 2)}.${digitsOnly.slice(2, 5)}.${digitsOnly.slice(5, 8)}/${digitsOnly.slice(8)}`;
    } else {
      return `${digitsOnly.slice(0, 2)}.${digitsOnly.slice(2, 5)}.${digitsOnly.slice(5, 8)}/${digitsOnly.slice(8, 12)}-${digitsOnly.slice(12, 14)}`;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Formatação especial para o campo de telefone
    if (name === 'phone') {
      // Aplica a formatação e atualiza o campo
      const formattedPhone = formatPhone(value);
      
      setFormData(prev => ({
        ...prev,
        [name]: formattedPhone
      }));
      
      // Valida o telefone
      if (value && !validatePhone(value)) {
        setPhoneError(t('validation.phoneInvalid'));
      } else {
        setPhoneError('');
      }
      
      return; // Interrompe a execução para evitar a atualização duplicada do estado
    }
    
    // Formatação especial para o campo de CNPJ
    if (name === 'cnpj') {
      // Aplica a formatação e atualiza o campo
      const formattedCNPJ = formatCNPJ(value);
      
      setFormData(prev => ({
        ...prev,
        [name]: formattedCNPJ
      }));
      
      // Valida o CNPJ
      if (value && !validateCNPJ(value)) {
        setCnpjError(t('validation.cnpjInvalid'));
      } else {
        setCnpjError('');
      }
      
      return; // Interrompe a execução para evitar a atualização duplicada do estado
    }
    
    // Validação de email
    if (name === 'email') {
      if (value && !validateEmail(value)) {
        setEmailError(t('validation.emailInvalid'));
      } else {
        setEmailError('');
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Verificar se há erros de validação antes de prosseguir
    let hasValidationError = false;
    
    // Verificar se todos os campos obrigatórios estão preenchidos
    if (!formData.name || !formData.email || !formData.cnpj || !formData.contact_name || !formData.phone) {
      hasValidationError = true;
      // O navegador já mostrará as mensagens para campos required
    }
    
    // Validar e-mail se não foi validado ainda
    if (!validateEmail(formData.email)) {
      setEmailError(t('validation.emailInvalid'));
      hasValidationError = true;
    }
    
    // Validar telefone
    if (!validatePhone(formData.phone)) {
      setPhoneError(t('validation.phoneInvalid'));
      hasValidationError = true;
    }
    
    // Validar CNPJ
    if (!validateCNPJ(formData.cnpj)) {
      setCnpjError(t('validation.cnpjInvalid'));
      hasValidationError = true;
    }
    
    // Se houver erros de validação, não prosseguir
    if (hasValidationError || emailError || phoneError || cnpjError) {
      return;
    }
    
    // Evita múltiplos cliques acidentais
    if (isSubmitting) {
      return;
    }
    
    setIsSubmitting(true);
    setShowConfirmModal(true);
  };

  const confirmSubmit = async () => {
    setShowConfirmModal(false);
    setIsLoading(true);

    try {
      const token = localStorage.getItem('@App:token');
      const apiData = {
        nameOrganization: formData.name,
        cnpjTaxId: formData.cnpj,
        email: formData.email,
        nameContact: formData.contact_name,
        phoneContact: formData.phone
      };

      if (id) {
        // Atualizar organização existente
        await api.patch(`/api/organization/${id}`, apiData, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      } else {
        // Criar nova organização
        await api.post('/api/organization', apiData, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }
      
      // Guarda informação no localStorage para mostrar a modal na lista
      localStorage.setItem('@App:showOrgSuccessModal', 'true');
      localStorage.setItem('@App:orgSuccessAction', id ? 'updated' : 'added');
      
      // Navega para a listagem
      navigate('/organizations');
      
    } catch (error) {
      console.error('Error saving organization:', error);
    } finally {
      setIsLoading(false);
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate('/organizations');
  };

  return (
    <Container>
      <Header>
        <Tooltip text={t('form.org.backToOrganizations')}>
          <BackButton onClick={handleBack}>
            {FiArrowLeft({ className: "icon", size: 30 })}
          </BackButton>
        </Tooltip>
      </Header>
      <FormContainer>
        <Title>{id ? t('form.org.editOrganization') : t('form.org.newOrganization')}</Title>
        <Form onSubmit={handleSubmit}>

        <InputGroup>
            <Label>{t('form.org.name')}</Label>
            <Input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder={t('form.org.namePlaceholder')}
              required
              disabled={isLoading}
            />
          </InputGroup>

          <InputGroup>
            <Label>{t('form.org.email')}</Label>
            <Input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={t('form.org.emailPlaceholder')}
              required
              disabled={isLoading}
              className={emailError ? 'error-input' : ''}
            />
            {emailError && (
              <div style={{ 
                color: theme.error, 
                fontSize: '12px', 
                marginTop: '5px' 
              }}>
                {emailError}
              </div>
            )}
          </InputGroup>

          <InputGroup>
            <Label>{t('form.org.cnpjTaxId')}</Label>
            <Input
              type="text"
              name="cnpj"
              value={formData.cnpj}
              onChange={handleChange}
              placeholder={t('form.org.cnpjPlaceholder')}
              disabled={isLoading}
              required
              className={cnpjError ? 'error-input' : ''}
              maxLength={18} // Comprimento máximo para o formato 00.000.000/0001-00
            />
            {cnpjError && (
              <div style={{ 
                color: theme.error, 
                fontSize: '12px', 
                marginTop: '5px' 
              }}>
                {cnpjError}
              </div>
            )}
          </InputGroup>

          <InputGroup>
            <Label>{t('form.org.contactName')}</Label>
            <Input
              type="text"
              name="contact_name"
              value={formData.contact_name}
              onChange={handleChange}
              placeholder={t('form.org.contactNamePlaceholder')}
              disabled={isLoading}
              required
            />
          </InputGroup>

          <InputGroup>
            <Label>{t('form.org.phone')}</Label>
            <Input
              type="text"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder={t('form.org.phonePlaceholder')}
              disabled={isLoading}
              className={phoneError ? 'error-input' : ''}
              maxLength={17} // Comprimento máximo para o formato +1 (xxx) xxx-xxxx
              required
            />
            {phoneError && (
              <div style={{ 
                color: theme.error, 
                fontSize: '12px', 
                marginTop: '5px' 
              }}>
                {phoneError}
              </div>
            )}
          </InputGroup>

          <SubmitButton type="submit" disabled={isLoading}>
            {isLoading ? <Loading /> : id ? t('form.org.update') : t('form.org.add')}
          </SubmitButton>
        </Form>
      </FormContainer>

      {showConfirmModal && (
        <DeleteModal>
          <DeleteModalContent>
            <h3>{id ? t('form.org.updateConfirmation') : t('form.org.addConfirmation')}</h3>
            <p>{id ? t('form.org.updateConfirmText') : t('form.org.addConfirmText')}</p>
            <DeleteModalButtons>
              <CancelButton onClick={() => setShowConfirmModal(false)}>
                {t('common.cancel')}
              </CancelButton>
              <GreenConfirmButton onClick={confirmSubmit}>
                {id ? t('form.org.update') : t('form.org.add')}
              </GreenConfirmButton>
            </DeleteModalButtons>
          </DeleteModalContent>
        </DeleteModal>
      )}
      {isLoading && <Loading />}
    </Container>
  );
};

export default OrganizationForm; 